import { useEffect, useRef, useState, type JSX } from 'react'
import { Play, Pause, Trash2, CheckCircle2, Phone, Clock } from 'lucide-react'
import RecordingMiniPlayer from '../admin/recordings/RecordingMiniPlayer.js'
import { getAvatarColor, getInitials } from '../agent/clients/ClientsTable.js'
import type { Voicemail } from '../../store/voicemailsStore.js'
import { formatInTimezone } from '../../utils/timezone.js'

export interface VoicemailRowProps {
  voicemail: Voicemail
  onMarkHeard?: (id: string) => void
  onDelete: (id: string) => void
}

const R = 'rounded-[3px]'

const STATUS_STYLES: Record<string, { badge: string; label: string }> = {
  NEW: { badge: 'border-blue-200 bg-blue-50 text-blue-700', label: 'New' },
  HEARD: { badge: 'border-slate-200 bg-slate-100 text-slate-500', label: 'Heard' },
  DELETED: { badge: 'border-red-200 bg-red-50 text-red-700 line-through', label: 'Deleted' },
}

function formatDate(iso: string): string {
  try {
    return formatInTimezone(iso, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function formatDuration(totalSec: number): string {
  const m = Math.floor(totalSec / 60)
  const s = totalSec % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

function VoicemailRow({ voicemail, onMarkHeard, onDelete }: VoicemailRowProps): JSX.Element {
  const { id, recordingId, durationMs, status, createdAt, transcript, callerName, callerNumber, audioUrl } = voicemail
  const durationSec = Math.round(durationMs / 1000)
  const receivedLabel = formatDate(createdAt)
  const statusStyle: { badge: string; label: string } = STATUS_STYLES[status] ?? { badge: 'border-slate-200 bg-slate-100 text-slate-500', label: status }

  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
      }
    }
  }, [])

  function togglePlay(): void {
    const audio = audioRef.current
    if (!audio) return
    if (playing) {
      audio.pause()
      setPlaying(false)
    } else {
      void audio.play()
      setPlaying(true)
    }
  }

  function handleTimeUpdate(): void {
    const audio = audioRef.current
    if (!audio || durationSec <= 0) return
    setProgress(Math.min(100, (audio.currentTime / durationSec) * 100))
  }

  function handleEnded(): void {
    setPlaying(false)
    setProgress(0)
  }

  return (
    <div
      className={`flex flex-col gap-3 ${R} border border-slate-200 bg-white p-4 shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-all hover:border-slate-300 hover:shadow-md`}
      data-testid="voicemail-row"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold ${getAvatarColor(callerNumber ?? callerName ?? id)}`}>
            {getInitials(callerName ?? null, callerNumber ?? 'vm')}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[13px] font-semibold text-slate-900">
                {callerName ?? 'Unknown Caller'}
              </span>
              <span
                className={`inline-flex items-center gap-1.5 border ${R} px-2 py-0.5 text-[11px] font-medium ${statusStyle.badge}`}
                data-testid="voicemail-status-badge"
                data-status={status}
              >
                {status === 'NEW' && <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />}
                {statusStyle.label}
              </span>
            </div>
            <div className="mt-0.5 flex items-center gap-3 text-[12px] text-slate-500">
              {callerNumber && (
                <span className="flex items-center gap-1 font-mono">
                  <Phone className="h-3 w-3 text-slate-400" />
                  {callerNumber}
                </span>
              )}
              <span className="flex items-center gap-1 font-mono">
                <Clock className="h-3 w-3 text-slate-400" />
                {formatDuration(durationSec)}
              </span>
              <span>{receivedLabel}</span>
            </div>
          </div>
        </div>
      </div>

      {audioUrl ? (
        <div className="flex items-center gap-2" data-testid="recording-mini-player" data-recording-id={recordingId} data-duration-sec={durationSec}>
          <button
            type="button"
            onClick={togglePlay}
            className={`flex h-8 w-8 shrink-0 items-center justify-center ${R} border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-[#3366FF]`}
            aria-label={playing ? 'Pause recording' : 'Play recording'}
          >
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </button>
          <div className={`h-1.5 flex-1 overflow-hidden ${R} bg-slate-200`} role="presentation">
            <div
              data-testid="mini-player-progress"
              className="h-full bg-[#3366FF] transition-[width] duration-200"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="shrink-0 font-mono text-[11px] text-slate-400">{formatDuration(durationSec)}</span>
          <audio
            ref={audioRef}
            src={audioUrl}
            onTimeUpdate={handleTimeUpdate}
            onEnded={handleEnded}
            preload="none"
          >
            <track kind="captions" />
          </audio>
        </div>
      ) : (
        <RecordingMiniPlayer recordingId={recordingId} durationSec={durationSec} />
      )}

      {transcript && (
        <p className="line-clamp-2 border-l-2 border-slate-200 pl-3 text-[13px] italic text-slate-500">&ldquo;{transcript}&rdquo;</p>
      )}

      <div className="flex items-center gap-2">
        {onMarkHeard && (
          <button
            type="button"
            onClick={() => onMarkHeard(id)}
            className={`inline-flex h-8 items-center gap-1.5 ${R} border border-blue-200 bg-blue-50 px-3 text-[12.5px] font-semibold text-blue-700 transition-colors hover:bg-blue-100`}
            aria-label={`Mark heard voicemail received ${receivedLabel}`}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            Mark heard
          </button>
        )}
        <button
          type="button"
          onClick={() => onDelete(id)}
          className={`inline-flex h-8 items-center gap-1.5 ${R} border border-rose-200 bg-white px-3 text-[12.5px] font-semibold text-rose-600 transition-colors hover:bg-rose-50`}
          aria-label={`Delete voicemail received ${receivedLabel}`}
        >
          <Trash2 className="h-3.5 w-3.5" />
          Delete
        </button>
      </div>
    </div>
  )
}

export default VoicemailRow
